import React, { useState, useEffect, useMemo, useCallback } from 'react';
import '../styles/Header.css';
import { translations } from '../translations';

const Header = ({
  showProjectDetail,
  showContactUs,
  showPrrProject,
  onBackToHome,
  onShowProjectDetail,
  onShowContactUs,
  onShowPrrProject,
  language,
  setLanguage
}) => {
  const t = translations[language];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const baseUrl = process.env.PUBLIC_URL || '';

  useEffect(() => {
    const headerImages = [
      `${baseUrl}/assets/DCM 2.png`,
      `${baseUrl}/assets/kannada-Logo.png`,
      `${baseUrl}/assets/English-Logo.png`
    ];
    headerImages.forEach(src => {
      const img = new Image();
      img.loading = 'eager';
      img.fetchPriority = 'high';
      img.src = src;
    });
  }, [baseUrl]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    setIsMenuOpen(false);

    if (sectionId === 'home' && (showProjectDetail || showContactUs || showPrrProject) && onBackToHome) {
      onBackToHome();
      return;
    }

    if (showProjectDetail || showContactUs || showPrrProject) {
      if (onBackToHome) onBackToHome();
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      // Calculate the total height of header + navbar dynamically
      const header = document.querySelector('.header');
      const navbar = document.querySelector('.navbar');
      const headerHeight = header ? header.offsetHeight : 150;
      const navbarHeight = navbar ? navbar.offsetHeight : 56;
      const headerOffset = headerHeight + navbarHeight + 20; // Add 20px for extra spacing

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }, [showProjectDetail, showContactUs, showPrrProject, onBackToHome]);

  const handleCompensationClick = useCallback(() => {
    if (onShowProjectDetail) onShowProjectDetail();
    setIsMenuOpen(false);
  }, [onShowProjectDetail]);

  const handleContactUsClick = useCallback(() => {
    if (onShowContactUs) onShowContactUs();
    setIsMenuOpen(false);
  }, [onShowContactUs]);

  const handlePrrProjectClick = useCallback(() => {
    if (onShowPrrProject) onShowPrrProject();
    setIsMenuOpen(false);
  }, [onShowPrrProject]);

  const logoSrc = useMemo(() => 
    language === 'kannada' 
      ? `${baseUrl}/assets/kannada-Logo.png` 
      : `${baseUrl}/assets/English-Logo.png`,
    [language, baseUrl]
  );

  const logoClass = useMemo(() => 
    language === 'kannada' ? 'logos-img-kannada' : 'logos-img-english',
    [language]
  );

  return (
    <>
      <header className="header">
        <div className="header-left">
          <div className="official-wrapper">
            <img 
              src={`${baseUrl}/assets/DCM 2.png`} 
              alt="Deputy Chief Minister" 
              className="profile-img dcm-img" 
              loading="eager" 
              fetchPriority="high" 
              decoding="async"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="official-name dcm-name">{t.header.dcmName}</span>
            <div className="official-title-wrapper">
              <span className="official-title dcm-title">{t.header.dcmTitle}</span>
              <span className="official-title-line2 dcm-title-line2">{t.header.dcmTitleLine2}</span>
            </div>
          </div>
        </div>
        <div className="header-center">
          <img 
            src={logoSrc}
            alt="ಬೆಂಗಳೂರು ಬ್ಯುಸಿನೆಸ್ ಕಾರಿಡಾರ್ - Bengaluru Business Corridor" 
            className={logoClass}
            loading="eager" 
            fetchPriority="high" 
            decoding="sync"
            onError={() => {}}
          />
        </div>
        <div className="header-right">
          <button 
            className={`lang-btn ${language === 'kannada' ? 'active' : ''}`}
            onClick={() => setLanguage('kannada')}
          >
            ಕನ್ನಡ
          </button>
          <button 
            className={`lang-btn ${language === 'english' ? 'active' : ''}`}
            onClick={() => setLanguage('english')}
          >
            English
          </button>
        </div>
      </header>

      <nav className="navbar">
        <div className="container">
          <div className="navbar-content">
            <div className={`navbar-menu ${isMenuOpen ? 'open' : ''}`}>
              <button className="nav-link" onClick={() => scrollToSection('home')}>{t.nav.home}</button>
              <button className="nav-link" onClick={() => scrollToSection('project')}>{t.nav.project}</button>
              <button className="nav-link" onClick={() => scrollToSection('interchanges')}>{t.nav.interchanges}</button>
              <button className="nav-link" onClick={() => scrollToSection('design')}>{t.nav.design}</button>
              <button className="nav-link" onClick={() => scrollToSection('mobility')}>{t.nav.mobility}</button>
              <button className="nav-link" onClick={() => scrollToSection('its')}>{t.nav.its}</button>
              <button className="nav-link" onClick={() => scrollToSection('environment')}>{t.nav.environment}</button>
              <button className="nav-link" onClick={() => scrollToSection('delivery')}>{t.nav.delivery}</button>
              <button className="nav-link" onClick={handleCompensationClick}>{t.nav.compensation}</button>
              <button className="nav-link" onClick={handlePrrProjectClick}>{t.nav.prrProject}</button>
              <button className="nav-link" onClick={handleContactUsClick}>{t.nav.contact}</button>
            </div>
            <button className="mobile-menu-toggle" onClick={toggleMenu}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 5h16"></path>
                <path d="M4 12h16"></path>
                <path d="M4 19h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
